import type { FC } from "react"
import { useEffect, useState } from "react"

import { motion, Reorder } from "framer-motion"
import { useRecoilValue, useRecoilState, atom, selector } from "recoil"
import styled from "styled-components"

import { captureClick } from "~/components/ui"

import {
  debugPanelIsOpenState,
  debugOrderState,
  debugVisualizationsEnabledState,
  localStorageEffect,
} from "./state"
import { useDivisionStepNavigation } from "./Visualizations/division-visualization-state"
import { useTracingStepNavigation } from "./Visualizations/tracing-visualization-state"
import { enablePanelOverlapValidationState } from "../../state/debug"
import { findNodeState } from "../../state/elements/nodes"
import { nodeSelectionState } from "../../state/elements/nodeSelection"
import { useComputeFaces } from "../../state/elements/transactions"

export const NodeEditor: FC<{ id: string }> = ({ id }) => {
  const [node, setNode] = useRecoilState(findNodeState(id))

  return (
    <div>
      <input
        type="number"
        value={node.x}
        onChange={(e) => setNode({ ...node, x: Number(e.target.value) })}
      />
      <input
        type="number"
        value={node.y}
        onChange={(e) => setNode({ ...node, y: Number(e.target.value) })}
      />
    </div>
  )
}

const Floater = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  bottom: 0;
  right: 0;
  width: fit-content;
  max-width: 350px;
  /* max-height: 600px; */
  /* padding: 10px 0; */
  background: #000a;
  backdrop-filter: blur(5px);
  border-top-left-radius: 8px;
  z-index: 100000;
  * {
    color: white;
  }
`

const ToggleButton = styled.button`
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  padding: 5px;
  width: 100%;
  z-index: 100000;
  font-size: var(--step--1);
  background-color: rgba(0, 0, 0, 0.14);
  transition: background-color 0.15s;
  :hover {
    background-color: #0000005a;
  }
`

export const BtnWrapper = styled.div`
  display: flex;
  gap: 5px;
`

export const OptionsWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  padding: 10px 20px;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 100px);
  button,
  label {
    font-size: var(--step--1);
  }
  input[type="text"] {
    border: 1px solid rgba(255, 255, 255, 0.298);
    border-radius: 5px;
    padding: 5px 15px;
    font-size: var(--step--2);
    ::placeholder {
      font-size: var(--step--2);
      color: rgba(255, 255, 255, 0.298);
    }
  }
  button {
    color: white;
    cursor: pointer;
    padding: 5px;
    width: 100%;
    z-index: 100000;
    transition: background-color 0.15s;
    background-color: rgba(0, 0, 0, 0.14);
    border-radius: 5px;
    color: rgba(255, 255, 255, 0.9);
    :hover {
      background-color: rgba(0, 0, 0, 0.353);
    }
  }
  input {
    :checked + label {
      color: rgba(255, 255, 255, 0.9);
    }
  }

  input[type="checkbox"] {
    -webkit-appearance: none;
    appearance: none;
    background-color: none;
    margin: 0;
    width: 15px;
    height: 15px;
    border: 0.05em solid rgba(255, 255, 255, 0.298);
    border-radius: 0.15em;
    transform: translateY(1px);
    display: grid;
    place-content: center;
    cursor: pointer;
  }
  input[type="checkbox"]::before {
    content: "";
    width: 10px;
    height: 10px;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    transform: scale(0);
    transform-origin: bottom left;
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em rgba(255, 255, 255, 0.9);
  }
  input[type="checkbox"]:checked::before {
    transform: scale(1);
  }
`

export const VisualizationToggleWrapper = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
  > ul {
    display: flex;
    flex-direction: column;
  }
  li {
    list-style: none;
  }
`

export const VisualizationToggle = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 10px;
  padding: 5px;
  border-radius: 5px;
  :hover {
    background-color: rgba(0, 0, 0, 0.14);
  }
  cursor: grab;
  input {
    cursor: pointer;
  }
  label {
    width: 100%;
    color: rgba(255, 255, 255, 0.6);
    cursor: grab;
  }
`

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 10px;
  border: 1px solid rgba(255, 255, 255, 0.209);
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 8px;
`
const nodeLabelSearchState = atom<string>({
  key: `nodeLabelSearch`,
  default: ``,
  effects: [localStorageEffect(`nodeLabelSearch`)],
})
export const nodeLabelSearchTermsState = selector<string[]>({
  key: `nodeLabelSearchTerms`,
  get: ({ get }) => get(nodeLabelSearchState).split(`,`),
})
export const enableFpsStatsState = atom<boolean>({
  key: `enableFpsStats`,
  default: false,
})

export const DesignerDebugPanel: FC = () => {
  const selectedNodes = useRecoilValue(nodeSelectionState)
  const computeFaces = useComputeFaces()
  const [goToPrevStep, goToNextStep] = useTracingStepNavigation()
  const [goToPrevStep0, goToNextStep0] = useDivisionStepNavigation()
  const [debugVisualizationsEnabled, setVisualizationsEnabled] = useRecoilState(
    debugVisualizationsEnabledState,
  )
  const [isOpen, setIsOpen] = useRecoilState(debugPanelIsOpenState)
  const [computationInterval, setComputationInterval] = useState<number | null>(
    null,
  )
  const [nodeLabelSearch, setNodeLabelSearch] =
    useRecoilState(nodeLabelSearchState)

  const [debugPanel, setDebugPanel] = useRecoilState(debugOrderState)
  const [panelValidation, setPanelValidation] = useRecoilState(
    enablePanelOverlapValidationState,
  )
  const [fpsStats, setFpsStats] = useRecoilState(enableFpsStatsState)

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null
    if (computationInterval) {
      intervalId = setInterval(() => {
        computeFaces()
      }, computationInterval * 1000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [computationInterval, computeFaces])

  return (
    <Floater onClick={captureClick}>
      {isOpen && (
        <OptionsWrapper>
          <SectionWrapper>
            {[...selectedNodes].map((nodeId) => (
              <NodeEditor id={nodeId} key={`devtool_editor_${nodeId}`} />
            ))}
            <input
              type="text"
              value={nodeLabelSearch}
              onChange={({ target: { value } }) => setNodeLabelSearch(value)}
            />
            <button onClick={computeFaces}>Recompute Faces</button>
            <input
              type="text"
              value={computationInterval ? String(computationInterval) : ``}
              placeholder="Computation Interval (s)"
              onChange={({ target: { value } }) =>
                !isNaN(Number(value))
                  ? setComputationInterval(Number(value))
                  : setComputationInterval(null)
              }
            />
            <label>Tracing Steps</label>
            <BtnWrapper>
              <button onClick={goToPrevStep}>Prev</button>
              <button onClick={goToNextStep}>Next</button>
            </BtnWrapper>
            <label>Intersection Steps</label>
            <BtnWrapper>
              <button onClick={goToPrevStep0}>Prev</button>
              <button onClick={goToNextStep0}>Next</button>
            </BtnWrapper>
          </SectionWrapper>
          <button onClick={() => setPanelValidation((current) => !current)}>
            Panel Overlap Validation {panelValidation ? `ON` : `OFF`}
          </button>
          <button onClick={() => setFpsStats((current) => !current)}>
            FPS Stats {fpsStats ? `ON` : `OFF`}
          </button>

          <VisualizationToggleWrapper layoutScroll>
            <Reorder.Group
              values={debugPanel}
              onReorder={setDebugPanel}
              layoutScroll
              axis="y"
            >
              {debugPanel.map((name) => {
                const displayName = name

                return (
                  <Reorder.Item key={displayName + `order`} value={name}>
                    <VisualizationToggle>
                      <input
                        type="checkbox"
                        id={displayName + `VisualizationToggle`}
                        checked={debugVisualizationsEnabled.has(displayName)}
                        onChange={() =>
                          setVisualizationsEnabled((current) => {
                            if (current.has(displayName)) {
                              current.delete(displayName)
                            } else {
                              current.add(displayName)
                            }

                            return new Set(current)
                          })
                        }
                      />
                      <label>{displayName}</label>
                    </VisualizationToggle>
                  </Reorder.Item>
                )
              })}
            </Reorder.Group>
          </VisualizationToggleWrapper>
        </OptionsWrapper>
      )}
      <ToggleButton onClick={() => setIsOpen((current) => !current)}>
        Debug Panel
      </ToggleButton>
    </Floater>
  )
}
